import api from "../api";
import {AbortableAsyncOperation, useAsyncOperations} from "./asyncOperation";
import {File as FileT, FileUploadSessionId, FileUploadSessionResponse, UploadOptions} from "platformed-client/types";
import {useMemo} from "react";
import {sleep} from "../utils/async";
import {getFrontendEnvironment} from "../utils/environment";

export const FILE_UPLOAD_LIMIT_BYTES = 30 * 1000 * 1000; // 30 MB (because Cloud Run has a limit of 32 MB)
export const LARGE_FILE_UPLOAD_LIMIT_BYTES = 2 * 1000 * 1000 * 1000; // 2 GB
const MAX_UPLOAD_RETRIES = 5;
const RETRY_DELAY_MS = 1000;

class LargeFileUploadError extends Error {
  status: number;
  constructor(xhr: XMLHttpRequest) {
    super(
      xhr.status === 0
        ? "Unknown network error during file upload"
        : `File upload failed with status ${xhr.status}: ${xhr.statusText}`,
    );
    this.status = xhr.status;
  }
}

export type FileUploadBaseState<TComplete> =
  | {id: "Uploading"; progress: number}
  | {id: "Uploaded"; result: TComplete}
  | {id: "Error"; error: unknown};

export abstract class FileUploadBase<TComplete = any> extends AbortableAsyncOperation<FileUploadBaseState<TComplete>> {
  #file: File;
  constructor(file: File) {
    super({id: "Uploading", progress: 0});
    this.#file = file;
  }
  get file() {
    return this.#file;
  }
  protected get uploadLimit(): number {
    return FILE_UPLOAD_LIMIT_BYTES;
  }

  async run() {
    if (this.#file.size === 0) {
      throw new Error("This file is empty (0 bytes). Please check the file and try again.");
    }
    if (this.#file.size > this.uploadLimit) {
      throw new Error(`File too large (max ${this.uploadLimit / (1000 * 1000)}MB)`);
    }
    const file = await this.upload(
      this.#file,
      progress => {
        this.state = {id: "Uploading", progress};
      },
      this.signal,
    );
    const result = await this.complete(file);
    this.state = {id: "Uploaded", result};
  }

  protected abstract upload(file: File, onProgress: (progress: number) => void, signal: AbortSignal): Promise<FileT>;
  protected abstract complete(file: FileT): Promise<TComplete>;

  protected errorState(error: unknown): FileUploadBaseState<TComplete> {
    return {id: "Error", error};
  }
}

export class FileUpload extends FileUploadBase<FileT> {
  #options: UploadOptions;

  constructor(file: File, options: UploadOptions = {system_wide: false}) {
    super(file);
    this.#options = options;
  }

  protected async upload(file: File, onProgress: (progress: number) => void, signal: AbortSignal): Promise<FileT> {
    return await api.files.upload(file, this.#options, onProgress, signal);
  }

  protected async complete(file: FileT): Promise<FileT> {
    return file;
  }
}

export abstract class LargeFileUploadBase extends FileUploadBase<FileT> {
  protected async upload(file: File, onProgress: (progress: number) => void, signal: AbortSignal): Promise<FileT> {
    const environment = getFrontendEnvironment().environment;

    if (environment === "development" || environment === "test") {
      // Upload sessions are not implemented in these environments, so just fall
      // back to the direct file upload approach.
      return await this.uploadDirect(file, onProgress, signal);
    } else {
      let lastError: unknown;
      for (let attempt = 0; attempt < MAX_UPLOAD_RETRIES; attempt++) {
        try {
          if (attempt > 0) {
            onProgress(0);
            await sleep(RETRY_DELAY_MS, signal);
          }
          const session = await this.createSession(file);
          await this.putFile(file, session.uri, onProgress, signal);
          return await this.completeSession(session.file_upload_session_id);
        } catch (error) {
          if (signal.aborted) throw error;
          // Retry unknown network errors (XHR status = 0)
          if (!(error instanceof LargeFileUploadError && error.status === 0)) throw error;
          lastError = error;
        }
      }
      throw lastError;
    }
  }

  protected get uploadLimit() {
    return LARGE_FILE_UPLOAD_LIMIT_BYTES;
  }

  /** PUTs the file to the given URI using XmlHttpRequest. */
  protected async putFile(
    file: File,
    uri: string,
    onProgress: (progress: number) => void,
    signal: AbortSignal,
  ): Promise<void> {
    // Make sure the signal isn't already aborted
    signal.throwIfAborted();

    const xhr = new XMLHttpRequest();
    const abort = () => {
      xhr.abort();
    };
    signal.addEventListener("abort", abort);

    if (onProgress) {
      xhr.upload.onprogress = ev => {
        onProgress(ev.loaded / ev.total);
      };
    }

    try {
      return await new Promise<void>((resolve, reject) => {
        xhr.onreadystatechange = () => {
          switch (xhr.readyState) {
            // Cancelled
            case 0:
              reject(signal.reason);
              break;
            // Completed
            case 4:
              if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
              } else {
                reject(new LargeFileUploadError(xhr));
              }
              break;
            default:
              break;
          }
        };

        xhr.withCredentials = false;
        xhr.open("PUT", uri, true);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.send(file);
      });
    } finally {
      signal.removeEventListener("abort", abort);
    }
  }

  protected async complete(file: FileT): Promise<FileT> {
    return file;
  }

  protected abstract createSession(file: File): Promise<FileUploadSessionResponse>;
  protected abstract completeSession(sessionId: FileUploadSessionId): Promise<FileT>;

  protected abstract uploadDirect(
    file: File,
    onProgress: (progress: number) => void,
    signal: AbortSignal,
  ): Promise<FileT>;
}

export class LargeFileUpload extends LargeFileUploadBase {
  #options: UploadOptions;

  constructor(file: File, options: UploadOptions = {system_wide: false}) {
    super(file);
    this.#options = options;
  }

  protected async createSession(file: File): Promise<FileUploadSessionResponse> {
    return await api.files.createUploadSession({name: file.name, mime_type: file.type, size: file.size});
  }

  protected async completeSession(sessionId: FileUploadSessionId): Promise<FileT> {
    return await api.files.completeUploadSession(sessionId);
  }

  protected async uploadDirect(
    file: File,
    onProgress: (progress: number) => void,
    signal: AbortSignal,
  ): Promise<FileT> {
    return await api.files.upload(file, this.#options, onProgress, signal);
  }
}

export type FileUploadResult<TFileUpload> = TFileUpload extends FileUploadBase<infer TResult> ? TResult : never;

export type FileUploadStatus = {
  /** At least one upload is actively in-flight. */
  uploading: boolean;
  /** At least one upload has errored. */
  hasErrors: boolean;
  /** Not all uploads have succeeded yet (uploading or has errors).
   *  Use this to block form submission in modals where the user
   *  should resolve errors before proceeding. */
  pending: boolean;
};

/** Subscribes to in-progress file uploads and returns the completed files plus a status summary. */
export function useFileUploads(uploads: FileUploadBase[]): [FileT[], FileUploadStatus] {
  const uploadStates = useAsyncOperations(uploads);
  const uploaded = useMemo(() => {
    const res = [];
    for (const uploadState of uploadStates) {
      if (uploadState.id === "Uploaded") {
        res.push(uploadState.result);
      }
    }
    return res;
  }, [uploadStates]);

  const status: FileUploadStatus = useMemo(() => {
    const uploading = uploadStates.some(s => s.id === "Uploading");
    const hasErrors = uploadStates.some(s => s.id === "Error");
    return {uploading, hasErrors, pending: uploading || hasErrors};
  }, [uploadStates]);

  return [uploaded, status];
}
